import { toast } from "@/lib/use-toast";

export interface ProcessFilesOptions {
  accept?: string[];
  maxSize?: number;
  maxFiles?: number;
  maxTotalSize?: number;
  existingFiles?: File[];
}

/**
 * Validates new files against type, per-file, count, and aggregate-size constraints.
 * Returns the files if valid, or null if validation fails (an alert is shown).
 */
export function processFiles(
  fileArray: File[],
  {
    accept,
    maxSize,
    maxFiles,
    maxTotalSize,
    existingFiles = [],
  }: ProcessFilesOptions,
): File[] | null {
  if (fileArray.length === 0) return null;

  if (
    maxFiles !== undefined &&
    existingFiles.length + fileArray.length > maxFiles
  ) {
    toast({
      title: "File Upload Error",
      description: `You can attach up to ${maxFiles} files.`,
      variant: "error",
    });
    return null;
  }

  if (
    maxTotalSize !== undefined &&
    [...existingFiles, ...fileArray].reduce(
      (total, file) => total + file.size,
      0,
    ) > maxTotalSize
  ) {
    const maxTotalSizeMB = (maxTotalSize / 1_000_000).toFixed(1);
    toast({
      title: "File Upload Error",
      description: `Attachments can total up to ${maxTotalSizeMB}MB.`,
      variant: "error",
    });
    return null;
  }

  if (maxSize !== undefined) {
    const oversizedFiles = fileArray.filter((file) => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      toast({
        title: "File Upload Error",
        description: `Some files exceed the maximum size of ${maxSizeMB}MB. Please select smaller files.`,
        variant: "error",
      });
      return null;
    }
  }

  if (accept && accept.length > 0) {
    const invalidFiles = fileArray.filter(
      (file) =>
        !accept.some((type) =>
          type.startsWith(".")
            ? file.name.toLowerCase().endsWith(type.toLowerCase())
            : file.type.match(type),
        ),
    );
    if (invalidFiles.length > 0) {
      toast({
        title: "File Upload Error",
        description: `Some files are not in the accepted formats: ${accept.join(", ")}`,
        variant: "error",
      });
      return null;
    }
  }

  return fileArray;
}
